<template>
  <form class="list-item-form" @submit.prevent="submit">
    <Field
      label="Internal Title"
      @input="$v.resource.key.$touch()"
      v-model="resource.key"
      :error="keyError"
      :placeholder="`e.g. ${listType.name} item 1`"
      helpText="This is for internal use to make it easy to identify the content. It will not be shown on your website."
    />

    <hr />

    <FileUploadForm
      v-if="listHasImages"
      label="Image"
      helpText="File types accepted: <strong>JPG, PNG</strong>"
      paramResource="list_item"
      paramName="image"
      @initFileUpload="initFileUpload"
      :fileUploading="fileUploading"
      :images="images"
      :key="_uid"
      :errors="errors"
      :stringUpdateFunction="stringUpdateFunction"
      :fileUpdateFunction="fileUpdateFunction">

       <Notification
          v-if="notification.active"
          :withinOverlay="true"
          :content="notification.content"
          :type="notification.type"
        />

      <div
        class="delete-btn"
        v-if="images.length"
        @click.capture="deleteImage">
        Delete Image
      </div>
    </FileUploadForm>

    <JsonFields
      v-if="fieldsLoaded"
      :highlightedField="highlightedField"
      :types="availableFields[listType.name]"
      :resource="value"
      @update="updateResource"
      @getPages="getPages"
      :pages="pages"
    />

    <Field
      hidden
      label="weight"
      @input="$v.resource.weight.$touch()"
      v-model="resource.weight"
      :error="weightError"
      helpText="This is the order which the menu item will be in the list"
    />

    <Field
      hidden
      class="list-value-field"
      label="value"
      type="textarea"
      @input="$v.resource.value.$touch()"
      v-model="resource.value"
      :disableEditor="true"
      :error="valueError"
    />

    <div class="btn-save">
      <Btn primary>Save Changes</Btn>
    </div>
  </form>
</template>

<script>
import {mapGetters} from 'vuex'

import form from '@/mixins/form'
import notification from '@/mixins/notification'
import availableFields from '@/mixins/availableFields'

import FileUploadForm from '@/components/forms/FileUploadForm'

import Btn from '@/components/Btn'

import {
  updateCompanySitePageListListItemData,
  updateCompanySitePageListListItemImage,
  deleteCompanySitePageListListItemImage
} from '@/lib/list_items'

import JsonFields from '@/components/forms/inputs/JsonFields'

import {errorMessages} from '@/lib/error_messages'
import {required} from 'vuelidate/lib/validators'

export default {
  name: 'ListItemForm',

  mixins: [form, notification, availableFields],

  props: {
    companyId: String,
    siteId: String,
    pageId: String,
    listId: String,
    listItemId: String,
    listType: Object,
    totalPages: String,
    highlightedField: String,
    pages: Array
  },

  computed: {
    ...mapGetters(['sessionToken']),
    ...errorMessages(['key', 'value', 'weight']),

    images() {
      return this.resource.image_versions || []
    },

    listHasImages() {
      return this.hasImages.includes(this.listType.name)
    },

    stringUpdateFunction() {
      const companyId = this.companyId
      const siteId = this.siteId
      const pageId = this.pageId
      const listId = this.listId
      const listItemId = this.listItemId

      return (image) => {
        return updateCompanySitePageListListItemData(
          companyId,
          siteId,
          pageId,
          listId,
          listItemId,
          image
        )
          .then(({data}) => {
            // computed is currying callback function to be used by upload form
            // eslint-disable-next-line vue/no-side-effects-in-computed-properties
            this.triggerNotification('success', 'image', 'updated')
            // eslint-disable-next-line vue/no-side-effects-in-computed-properties
            this.fileUploading = false
            // eslint-disable-next-line vue/no-side-effects-in-computed-properties
            this.$emit('getListItemData')

            return data
          })
          .catch(() => {})
      }
    },

    fileUpdateFunction() {
      const companyId = this.companyId
      const siteId = this.siteId
      const pageId = this.pageId
      const listId = this.listId
      const listItemId = this.listItemId
      const token = this.sessionToken

      return (image) => {
        return updateCompanySitePageListListItemImage(
          companyId,
          siteId,
          pageId,
          listId,
          listItemId,
          image,
          token
        )
          .then(({data}) => {
            // computed is currying callback function to be used by upload form
            // eslint-disable-next-line vue/no-side-effects-in-computed-properties
            this.triggerNotification('success', 'image', 'updated')
            // eslint-disable-next-line vue/no-side-effects-in-computed-properties
            this.fileUploading = false
            // eslint-disable-next-line vue/no-side-effects-in-computed-properties
            this.$emit('getListItemData')

            return data
          })
          .catch(({response}) => {
            // eslint-disable-next-line vue/no-side-effects-in-computed-properties
            this.errors = response.errors
          })
      }
    }
  },

  data() {
    return {
      fieldsLoaded: false,
      fileUploading: false,
      hasImages: [
        'slideshow',
        'feature-blocks',
        'text-image',
        'image-cards',
        'image-grid',
        'basic-gallery',
        'mini-carousel',
        'team-members',
        'product-list',
        'landing-form'
      ]
    }
  },

  components: {
    Btn,
    JsonFields,
    FileUploadForm
  },

  validations: {
    resource: {
      key: {required},
      value: {required},
      weight: {required}
    }
  },

  methods: {
    updateResource(value) {
      this.resource.value = value
    },

    getPages() {
      this.$emit('getPages')
    },

    initFileUpload() {
      this.fileUploading = true
    },

    deleteImage() {
      const companyId = this.companyId
      const siteId = this.siteId
      const pageId = this.pageId
      const listId = this.listId
      const listItemId = this.listItemId

      deleteCompanySitePageListListItemImage(
        companyId,
        siteId,
        pageId,
        listId,
        listItemId
      ).then(() => {
        this.resource.image_versions = []
        this.triggerNotification('success', 'image', 'deleted')
      })
    }
  }
}
</script>

<style lang="sass">
@import '../../stylesheets/base/variables'

.item-title
  margin-bottom: $gap-x-large
</style>
